import { Component,computed, Input, effect, NgZone, untracked, signal, Injector, ChangeDetectionStrategy, runInInjectionContext, WritableSignal, Output, EventEmitter } from '@angular/core';
import {
    OriTable,
    OriTableBody,
    OriTableCell,
    OriTableHead,
    OriTableHeader,
    OriTableRow
} from '@origin-ui/components/table';
import { cn } from '@origin-ui/components/utils';
import {
    ColumnDef,
    createAngularTable,
    flexRenderComponent,
    FlexRenderDirective,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    PaginationState,
    ColumnPinningState,
    Column,
    SortingState,
    Table
} from '@tanstack/angular-table';
import {
    ChevronDown,
    ChevronFirst,
    ChevronLast,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    ChevronsUpDown,
    LucideAngularModule,
} from 'lucide-angular';
import { OriButton } from '~/components/ui/button';
import { OriLabel } from '~/components/ui/label';
import { OriPagination, OriPaginationContent } from '~/components/ui/pagination';
import { OriSelectNative } from '~/components/ui/select-native';
import { SearchBarComponent } from '~/components/veriskComponents/reusable_ui/search_bar'
import { EditColumnComponent } from '~/components/veriskComponents/reusable_ui/column_customize';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import * as Config from './Config';
import { ColumnEditConfig } from '../configModel';

@Component({
  selector: 'verisk-table-component',
  imports: [
    OriTableHeader,
    OriTableRow,
    OriTableHead,
    LucideAngularModule,
    FlexRenderDirective,
    OriTableBody,
    OriTableCell,
    OriTable,
    OriTableBody,
    OriTableCell,
    OriTableRow,
    OriPagination,
    OriPaginationContent,
    OriButton,
    OriSelectNative,
    OriLabel,
    SearchBarComponent,
    EditColumnComponent,
    CommonModule,
    NgxSkeletonLoaderModule,
  ],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './veriskTable.component.html',
  styleUrls: ['./veriskTable.component.scss'],
  standalone: true,
})
export class vTable<T extends {id: string | number}, C extends object =any> {

  readonly customStyles = computed(() => {
    const styles = this.config().customStyles || {};
    return {
      // Container styles
      '--table-container-bg': styles.containerBg || 'hsl(var(--background))',
      '--table-container-border': styles.containerBorder || '1px solid hsl(var(--border))',
      '--table-container-border-radius': styles.containerBorderRadius || '6px',
      '--table-container-shadow': styles.containerShadow || 'none',
      
      // Table styles
      '--table-bg': styles.tableBg || 'transparent',
      '--table-border': styles.tableBorder || 'none',
      '--table-border-radius': styles.tableBorderRadius || '20px',
      
      // Header styles
      '--table-header-bg': styles.headerBg || 'white',
      '--table-header-text-color': styles.headerTextColor || 'inherit',
      '--table-header-border-bottom': styles.headerBorderBottom || '1px solid oklch(87.2% 0.01 258.338)',
      '--table-header-font-weight': styles.headerFontWeight || '500',
      '--table-header-font-size': styles.headerFontSize || '0.875rem',
      '--table-header-padding': styles.headerPadding || '0.5rem',
      '--table-header-height': styles.headerHeight || '2.75rem',
      
      // Row styles
      '--table-row-bg': styles.rowBg || 'transparent',
      '--table-row-hover-bg': styles.rowHoverBg || 'rgb(239 246 255)',
      '--table-row-selected-bg': styles.rowSelectedBg || 'rgb(219 234 254)',
      '--table-row-border-bottom': styles.rowBorderBottom || 'none',
      '--table-row-text-color': styles.rowTextColor || 'inherit',
      '--table-row-font-size': styles.rowFontSize || '0.875rem',
      
      // Cell styles
      '--table-cell-padding': styles.cellPadding || '12px',
      '--table-cell-border-right': styles.cellBorderRight || 'none',
      '--table-cell-text-align': styles.cellTextAlign || 'left',
      
      // Button styles
      '--table-button-bg': styles.buttonBg || 'transparent',
      '--table-button-hover-bg': styles.buttonHoverBg || 'hsl(var(--accent))',
      '--table-button-text-color': styles.buttonTextColor || 'inherit',
      '--table-button-border': styles.buttonBorder || '1px solid hsl(var(--border))',
      '--table-button-border-radius': styles.buttonBorderRadius || '6px',
      '--table-button-padding': styles.buttonPadding || '0.5rem',
      
      // Pagination styles
      '--table-pagination-bg': styles.paginationBg || 'transparent',
      '--table-pagination-border': styles.paginationBorder || 'none',
      '--table-pagination-text-color': styles.paginationTextColor || 'hsl(var(--muted-foreground))',
      '--table-pagination-button-bg': styles.paginationButtonBg || 'transparent',
      '--table-pagination-button-hover-bg': styles.paginationButtonHoverBg || 'hsl(var(--accent))',

    };
  });




  // Utility/classNames function used for conditional styling
  protected readonly cn = cn;
  // Icon components used for sorting, pagination, or navigation controls in the table UI
  protected readonly ChevronUp = ChevronUp;
  protected readonly ChevronDown = ChevronDown;
  protected readonly ChevronsUpDown = ChevronsUpDown;
  protected readonly ChevronLeft = ChevronLeft;
  protected readonly ChevronRight = ChevronRight;
  protected readonly ChevronFirst = ChevronFirst;
  protected readonly ChevronLast = ChevronLast;

  
  // Accept table configuration options
  @Input() tableOptions: Partial<Config.TableOptions> = {};
  @Input() columnEditConfig: ColumnEditConfig | null = null;

  @Input() isLoading!: boolean;
  // When true, uses simplified pagination (fixed 10 rows, right-aligned controls)
  @Input() simplePagination: boolean = false;


  // Reactive signal defining the tables row config from the db
  @Input({ required: true}) rowData!: WritableSignal<T[]>;
  // Reactive signal defining the initial column data received from db
  @Input({required: true}) colData!: WritableSignal<C[]>;

  @Input({required: false}) rowUpdate!: WritableSignal<T[]>;
  // Reactive signal defining the tables column configuration (initially empty)
  readonly columns = signal<ColumnDef<T>[]>([]);

    // Add output event for cell changes
  @Output() cellChange = new EventEmitter<{
    field: string;
    value: any;
    rowData: any;
    eventType: 'blur' | 'change' | 'click'
  }>();
    
  // Emits the internal TanStack table signal so parent components can reuse utilities like `column-edit`
  @Output() tableSignalReady = new EventEmitter<WritableSignal<Table<any>>>();
  // Emits sorting state changes so parent can handle server-side sorting
  @Output() sortingChange = new EventEmitter<SortingState>();
  // Emits when columns have been rebuilt and are ready
  @Output() columnsReady = new EventEmitter<void>();

  // Reactive signal managing pagination state:
  // - `pageIndex`: the current page (starting at 0)
  // - `pageSize`: how many rows to show per page
  readonly pagination = signal<PaginationState>({
      pageIndex: 0,
      pageSize: 10
  });

  // Row selection state (conditionally initialized)
  readonly rowSelectionState = signal<Record<string, boolean>>({});

  // Computed configuration that merges defaults with provided options
  readonly config = computed(() => {
    const mergedConfig = { ...Config.DEFAULT_TABLE_OPTIONS, ...this.tableOptions };

    return mergedConfig;
  });

  // Cached computed signal for current page's ending row index
  readonly currentPageEnd = computed(() => {
    const pagination = this.table().getState().pagination;
    const rowCount = this.table().getRowCount();

    const pageEnd = pagination.pageIndex * pagination.pageSize + pagination.pageSize;
    return Math.min(Math.max(pageEnd, 0), rowCount);
  })

  // Reactive signal for current sorting state (empty means no sorting applied)
  readonly sorting = signal<SortingState>([]);

  // Global filter signals
  readonly globalFilter = signal('');
  readonly debouncedGlobalFilter = signal('');
  @Input() customGlobalFilter = signal('');

  readonly columnPinning = signal<ColumnPinningState>({});

  readonly getCommonPinningStyles = (
    column: Column<T>, index?: number
  ): Record<string, any> => {
    const isPinned = column.getIsPinned()
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left')
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right')
    return {
      // backgroundColor: isPinned  ? 'white' : 'transparent',
      background: index === 0  ? 'white': 'rgba(245, 245, 245, .9)',
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      position: isPinned ? 'sticky' : 'relative',
      width: `${column.getSize()}px`,
      zIndex: isPinned ? 1 : 0,
    }
  }

  // A special column that adds checkboxes for row selection
  readonly selectionColumn: ColumnDef<T> = {
    id: 'select',
    header: ({ column, table}) => {
      return flexRenderComponent(Config.CheckboxHeader);
    },
    cell: ({getValue, row, column, table}) => {
      return flexRenderComponent(Config.CheckboxRow)
    },
    size: 50,
    minSize: 50,
    maxSize: 50,
    enableResizing: false,
  }

  // A special column that renders actions in each row
  readonly actionColumn: ColumnDef<T> = {
    id: 'action',
    cell: () => flexRenderComponent(Config.ActionRow),
    size: 50,
    enableResizing: false,
    minSize: 50,
    maxSize: 50,
  }

  // Reactive computed property that tracks metadata related to column resizing
  readonly columnSizingInfo = computed(() => {
    return this.table() ? this.table().getState().columnSizingInfo : {};
  });
  
  // Reactive computed property that holds the current width values for each column
  readonly columnSizing = computed(() => {
    return this.table() ? this.table().getState().columnSizing : {};
  });

  // Dynamic default column based on configuration
  readonly defaultColumn = computed<Partial<ColumnDef<T>>>(() => {
    const cfg = this.config();
    
    if (!cfg.enableCellEditing) {
      // Return simple display-only cell
      return {
        cell: ({ getValue }) => getValue() as any
      };
    }

    // Return editable cell (original behavior)
    return {
      cell: ({ getValue, row, column, table}) => {
        const initialValue = getValue();
        return flexRenderComponent(Config.EditableCell, {
          inputs: {
            value: initialValue,
          },
          outputs: {
            blur: value => {
              if (table.options.meta?.updateData) {
                table.options.meta.updateData(row.index, column.id, value);
              }
            },
          },
        })
      },
    };
  });

  // Whether to automatically reset to the first page after data changes
  readonly autoResetPageIndex = signal(false);
  
  // Holds a filtered or transformed copy of the table data (used for display or export)
  resRender: T[] = [];
  // Flag to indicate a loading state
  colWait = true;
  rowWait = true;

  // Computed CSS variable map for dynamically applying column sizes in the UI
  readonly columnSizeVars = computed(() => {
    if (!this.table()) return {};
    void this.columnSizing();
    void this.columnSizingInfo();

    const headers = untracked(() => this.table()!.getFlatHeaders());
    const colSizes: { [key: string]: number } = {};
    for (const header of headers) {
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  });

  readonly rowsReady = computed(() => this.table().getRowModel().rows.length > 0);

  table!: WritableSignal<Table<T>>;

  constructor(
    private injector: Injector,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.createTableInstance();

    runInInjectionContext(this.injector, ()=> {
      effect((onCleanup) => {
        let value: string;
        if (this.config().enableGlobalFilter) {
          value = this.globalFilter();
        }else if(this.config().enableCustomGlobalFilter && this.customGlobalFilter){
          value = this.customGlobalFilter();
        }
          const timeout = setTimeout(() => {
            this.debouncedGlobalFilter.set(value);
          }, 400);
          onCleanup(() => clearTimeout(timeout));
        
      })
      
      let previous: T[] | null = null;
      let previousCol: C[] | null = null;
      
      effect(() => {
        const data = this.colData();
        if (data !== previousCol) {
          this.modifyCol();
          this.colWait = false;
          previousCol = data;
        }
      })
      
      effect(() => {
        const data = this.rowData();
        if (!this.isLoading) {
          this.rowWait = false;
        }
        if (data != previous) {
          if (this.rowsReady()){
            this.rowWait = false;
          } 
        }
      })
    })
  }

  createTableInstance() {
    const cfg = this.config();
    
    // Initialize pagination with configured page size
    this.pagination.set({
      pageIndex: 0,
      pageSize: this.simplePagination ? 10 : (cfg.initialPageSize! || 10)
    });

    // Build row models array based on enabled features
    const getRowModels = () => {
      const models: any = {
        getCoreRowModel: getCoreRowModel(),
      };
      
      if (cfg.enableSorting) {
        models.getSortedRowModel = getSortedRowModel();
      }
      
      if (cfg.enablePagination) {
        models.getPaginationRowModel = getPaginationRowModel();
      }

      if (cfg.enableFiltering || cfg.enableGlobalFilter || cfg.enableCustomGlobalFilter) {
        models.getFilteredRowModel = getFilteredRowModel();
      }

      
      return models;
    };

    // Build state object based on configuration
    const getState = () => {
      const state: any = {};

      if (cfg.enableGlobalFilter || cfg.enableCustomGlobalFilter) {
        state.globalFilter = this.debouncedGlobalFilter();
      }

      if (cfg.enableSorting) {
        state.sorting = this.sorting();
      }
      
      if (cfg.enablePagination) {
        state.pagination = this.pagination();
      }

      if (cfg.enableRowSelection) {
        state.rowSelection = this.rowSelectionState();
      }

      return state;
    };

    // Build event handlers based on configuration
    const getEventHandlers = () => {
      const handlers: any = {};
      
      if (cfg.enableGlobalFilter) {
        handlers.onGlobalFilterChange = (filter: string) => this.globalFilter.set(filter);
      } else if(cfg.enableCustomGlobalFilter) {
        handlers.onGlobalFilterChange = (filter: string) => this.customGlobalFilter.set(filter);
      }
      
      if (cfg.enableSorting) {
        handlers.onSortingChange = (updaterOrValue: any) => {
          typeof updaterOrValue === 'function'
            ? this.sorting.update(updaterOrValue)
            : this.sorting.set(updaterOrValue);
          this.sortingChange.emit(this.sorting());
        };
      }
      
      if (cfg.enablePagination) {
        handlers.onPaginationChange = (updater: any) =>
          typeof updater === 'function' 
            ? this.pagination.update(updater) 
            : this.pagination.set(updater);
      }
      
      if (cfg.enableRowSelection) {
        handlers.onRowSelectionChange = (updaterOrValue: any) =>
          typeof updaterOrValue === 'function'
            ? this.rowSelectionState.update(updaterOrValue)
            : this.rowSelectionState.set(updaterOrValue);
        handlers.enableRowSelection = true;
        handlers.enableMultiRowSelection = cfg.enableMultiRowSelection;
      }
      
      return handlers;
    };

    this.table = signal(
      createAngularTable<T>(() => ({
        data: this.rowData(),
        columns: this.columns(),
        defaultColumn: this.defaultColumn(),
        
        // Conditionally set row expansion
        ...(cfg.enableRowExpansion && {
          getRowCanExpand: () => true
        }),

        // Row models
        ...getRowModels(),

        // Global filter configuration
        ...((cfg.enableGlobalFilter || cfg.enableCustomGlobalFilter) ? { globalFilterFn: cfg.globalFilterFn } : {}),

        // Event handlers
        ...getEventHandlers(),

        // State management
        state: getState(),

        
        // Column features
        ...(cfg.enableColumnPinning && { enableColumnPinning: true }),
        ...(cfg.enableColumnResizing && {
          enableColumnResizing: true,
          columnResizeMode: cfg.columnResizeMode
        }),

        // Debug configuration
        ...(cfg.enableDebug && {
          debugTable: cfg.debugTable,
          debugHeaders: cfg.debugHeaders,
          debugColumns: cfg.debugColumns
        }),


        // Other options
        autoResetPageIndex: cfg.autoResetPageIndex,

        // Custom metadata with conditional actions
        meta: this.buildTableMeta(cfg)
      }))
    );
        
    // Allow parent components to drive column customization UI outside this component
    this.tableSignalReady.emit(this.table as unknown as WritableSignal<Table<any>>);
  }

  // Separate method to build meta object based on configuration
  private buildTableMeta(config: Config.TableOptions) {
    const meta: any = {};

    if (config.enableCustomActions) {
      meta.updateData = (rowIndex: number, columnId: string, value: unknown) => {
        this.autoResetPageIndex.set(false);
        
        this.ngZone.runOutsideAngular(() => {
          this.rowData.update((old) =>
            old.map((row: T, index: number) => {
              if (index === rowIndex) {
                return {
                  ...row,
                  [columnId as keyof T]: value,
                };
              }
              return row;
            })
          );
          const data = this.table().getRowModel().rows[rowIndex].original;
          console.log(data);
          this.rowUpdate.set([data]);
        });
      };

      meta.delSelectedRow = () => {
        const selectedRows = this.table().getSelectedRowModel().flatRows.map(row => ({
          ...row.original,
          softDel: true,
        }));
        
        const selectedRowIds = this.table().getSelectedRowModel().flatRows.map(row => row.original.id);
        this.resRender = this.rowData();
        this.resRender = this.resRender.filter(item => !selectedRowIds.includes(item.id));
        this.rowData.set(this.resRender);
        this.rowUpdate.set(selectedRows);
      };

      meta.delRow = (rowIndex: number, columnId: string, value: unknown) => {
        const row = [this.table().getRowModel().rowsById[rowIndex]].map(row => ({
          ...row.original,
          softDel: true,
        }));
        
        this.rowUpdate.set(row);
        this.rowData.update((old) => old.filter((_, index) => index !== rowIndex));
      };
    }

    return meta;
  }

  private modifyCol() {
    this.ngZone.runOutsideAngular(() => {
      const arr: ColumnDef<T>[] = [];
      const response = this.colData();
      const cfg = this.config();

      response.forEach(element => {
        // Type guard for 'type' property
        if ('type' in element && (element as any).type === 'dropdown') {
          const options = 'options' in element && Array.isArray((element as any).options)
            ? (element as any).options
            : [];
          arr.push({
            id: (element as any).field,
            accessorKey: (element as any).field,
            header: (element as any).label,
            size: (element as any).width,
            ...((element as any).enableSorting === false ? { enableSorting: false } : {}),
            // enableSorting: false,
            cell: ({ getValue, row, column, table }) => flexRenderComponent(Config.DropdownCellComponent, {
              inputs: {
                value: getValue(),
                options: options,
              },
              outputs: {
                blur: (value: string) => {
                  table.options.meta?.updateData(row.index, column.id, value);
                },
              }
            }),
            minSize: (element as any).width / 2,
            maxSize: (element as any).width + 5,
            meta: {
              colType: (element as any).type,
            }
          } satisfies ColumnDef<T>);
        } else if ('type' in element && (element as any).type === 'text') {
          const col: any = {
            id: (element as any).field,
            accessorKey: (element as any).field,
            header: (element as any).label,
            size: (element as any).width,
            minSize: (element as any).width / 2,
            maxSize: (element as any).width + 100,
            ...((element as any).enableSorting === false ? { enableSorting: false } : {}),
            meta: {
              colType: (element as any).type,
            }
          };
          if (!cfg.enableCellEditing) {
            col.cell = ({ getValue }: any) => flexRenderComponent(Config.TruncatedTextTooltip, {
              inputs: {
                value: getValue(),
              },
            });
          }
          arr.push(col as ColumnDef<T>);
        } else if ('type' in element && (element as any).type === 'status') {
          arr.push({
            id: (element as any).field,
            accessorKey: (element as any).field,
            header: (element as any).label,
            ...((element as any).enableSorting === false ? { enableSorting: false } : {}),
            cell: ({getValue,row, column, table}) => flexRenderComponent(Config.StatusCellComponent, {
              inputs: {
                value: getValue(),
              },
            }),
            size: (element as any).width,
            minSize: (element as any).width / 2,
            maxSize: (element as any).width + 100,
            meta: {
              colType: (element as any).type,
            }
          } satisfies ColumnDef<T>);
        } else if ('type' in element && (element as any).type === 'link') {
          arr.push({
            id: (element as any).field,
            accessorKey: (element as any).field,
            header: (element as any).label,
            ...((element as any).enableSorting === false ? { enableSorting: false } : {}),
            cell: ({ getValue, row, column, table }) => flexRenderComponent(Config.LinkCellComponent, {
              inputs: {
                value: getValue(),
                products: row.original
              },
              outputs: {
                click: (value: String) => {
                  this.cellChange.emit({
                    field: (element as any).field,
                    value: value,
                    rowData: row.original,
                    eventType: 'click'
                  });
                }
              }
            }),
            size: (element as any).width,
            minSize: (element as any).width / 2,
            maxSize: (element as any).width + 100,
            meta: {
              colType: (element as any).type,
            }
          } satisfies ColumnDef<T>);
        }

      });

      this.ngZone.run(() => {
        if (response) {
          // Build columns array based on configuration
          const finalColumns: ColumnDef<T>[] = [];
          
          if (cfg.enableRowSelection) {
            finalColumns.push(this.selectionColumn);
          }
          
          finalColumns.push(...arr);

          if (cfg.showActionMenu) {
            finalColumns.push(this.actionColumn);
          }
          
          this.columns.set(finalColumns);
        }
        this.setPinningState();
        this.columnsReady.emit();
      })
    })
  }

  setPinningState() {
    const table = this.table();
    const cfg = this.config();
    
    if (!table || !cfg.enableColumnPinning) return;

    const pinning: ColumnPinningState = {};

    if (cfg.enableRowSelection && !cfg.enableCustomColPinning) {
      pinning.left = ['select'];

    } else if (cfg.enableCustomColPinning) {
      pinning.left = [cfg.enableCustomColPinning];
    }

    if (cfg.showActionMenu) {
      pinning.right = ['action'];
    }
    
    table.setColumnPinning(pinning);
  }


  /**
   * Updates the page size of the table when the user selects a new value.
   * Triggered from a <select> input bound to pagination size options.
   *
   * @param event - DOM change event from the <select> element
   */
  handlePageSizeChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.table().setPageSize(Number(value)); // Applies new page size and triggers re-pagination
  }

  // Simple pagination helpers
  get simpleTotalPages(): number {
    try {
      return this.table().getPageCount?.() ?? Math.ceil(this.table().getRowCount() / this.pagination().pageSize);
    } catch {
      return 1;
    }
  }

  get simpleCurrentPage(): number {
    return (this.table().getState().pagination.pageIndex ?? 0) + 1;
  }

  simplePageNumbers(): number[] {
    const total = this.simpleTotalPages;
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    const current = this.simpleCurrentPage;
    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }

  simpleGoTo(page: number) {
    const target = Math.min(Math.max(page, 1), this.simpleTotalPages) - 1;
    this.table().setPageIndex(target);
  }

  simplePrev() {
    if (this.table().getCanPreviousPage()) this.table().previousPage();
  }

  simpleNext() {
    if (this.table().getCanNextPage()) this.table().nextPage();
  }

  /**
   * Enables keyboard accessibility for sortable table headers.
   * Responds to Enter or Space key presses to toggle column sorting,
   * mimicking click behavior for screen readers and keyboard users.
   *
   * @param e - KeyboardEvent triggered on header element
   * @param header - Header object representing the current column
   */
  onHeaderKeyDown(e: any, header: any) {
    // Enhanced keyboard handling for sorting
    if (header.column.getCanSort() && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault(); // Prevents scrolling (Space) or form submission (Enter)
      header.column.getToggleSortingHandler()?.(e); // Invokes sort toggle logic
    }
  }

  onRowClick(event: MouseEvent, row: any) {
    if (event.defaultPrevented || event.button !== 0) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const interactiveSelector = 'button, a, input, select, textarea, label, [data-prevent-row-click]';
    if (target.closest(interactiveSelector)) {
      return;
    }

    this.cellChange.emit({
      field: '__rowClick__',
      value: row?.original ?? null,
      rowData: row?.original ?? null,
      eventType: 'click',
    });
  }

}














